import React, { useEffect, useRef, useState } from "react";
import CusSelect from "../../mui/cus_select/CusSelect";
import "./find-game.scss";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setSaveSiteBeforeGame } from "../../../store/commonSlice";
import useNavigateWithSearch from "../../../hooks/useNavigateWitchSearch";
import useChooseMode from "../../../hooks/useChooseMode";
import IconSearch from "../../mui/JSXIcons/IconSearch";
import IconArrow from "../../mui/JSXIcons/IconArrow";
import { API_CASINO } from "../../../api/casino";
import { checkBonusGame } from "../../../utils/utils";
import { useAppSelector } from "../../../store/index.ts";
import ShowContainer from "../../mui/ShowContainer.jsx";
import CusImage from "../../custom_image/CusImage.jsx";
import { setFindMobile } from "../../../store/siteSlice.js";
import useHighlightMatch from "../../../hooks/useHighlightMatch.js";

export default function FindGame({
  autoFocus,
  isOpenMobileFind,
  inputId,
  setQuery,
  setMyGamesOptions
}) {
  const navigate = useNavigateWithSearch();
  const dispatch = useDispatch();
  const highlightMatch = useHighlightMatch();
  const { onChoseMode } = useChooseMode();
  const { t } = useTranslation();
  const currency = useAppSelector((state) => state.user.currency);
  const isExtraSmall = useAppSelector((state) => state.deviceSize.isExtraSmall);

  const [inputValue, setInputValue] = useState("");
  const [gamesOptions, setGamesOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [textInput, setTextInput] = useState("");
  // счётчик запросов, чтобы игнорировать устаревший ответ, если пользователь уже ввёл другое
  const requestIdRef = useRef(0);

  const tmSearchRef = useRef(null);
  const findRef = useRef(null);
  const containerRef = useRef(null);

  // открываем свой попап при клике/фокусе на инпут (CusSelect не пробрасывает onFocus)
  useEffect(() => {
    const node = findRef.current;
    if (!node) return;

    const handleOpen = () => setOpen(true);
    node.addEventListener("focus", handleOpen);
    node.addEventListener("click", handleOpen);
    return () => {
      node.removeEventListener("focus", handleOpen);
      node.removeEventListener("click", handleOpen);
    };
  }, []);

  // закрываем попап по клику вне компонента
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const onSearch = (value) => {
    const requestId = ++requestIdRef.current;
    setIsSearching(true);

    API_CASINO.list
      .search({ sample: value, currency })
      .then((e) => {
        // если пользователь успел ввести что-то новое — старый ответ игнорируем
        if (requestId !== requestIdRef.current) return;

        const list = e.data.list;

        const alreadyLabel = new Set();
        const gameOptions = list.map((el) => {
          let label = `${el.name} ${el.provider}`;
          if (alreadyLabel.has(label)) label += `(${el.gameId})`;
          alreadyLabel.add(label);

          return {
            value: el.gameId,
            label,
            name: el.name,
            provider: el.provider,
            category: el.category || el.type || t("Игра"),
            image: el.image,
            isBonus: el.isBonus
          };
        });

        setGamesOptions(gameOptions);
        setMyGamesOptions?.(gameOptions);
      })
      .catch(console.error)
      .finally(() => {
        if (requestId === requestIdRef.current) setIsSearching(false);
      });
  };

  const onMyInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setQuery?.(value);
    setTextInput(value);
    setOpen(true);

    if (value.length >= 3) {
      clearTimeout(tmSearchRef.current);
      tmSearchRef.current = setTimeout(() => onSearch(value), 500);
    } else {
      clearTimeout(tmSearchRef.current);
      requestIdRef.current++; // отменяем возможный ответ предыдущего поиска
      setIsSearching(false);
      setGamesOptions([]);
      setMyGamesOptions?.([]);
    }
  };

  const onClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(tmSearchRef.current);
    requestIdRef.current++;
    setInputValue("");
    setIsSearching(false);
    setGamesOptions([]);
    setMyGamesOptions?.([]);
    findRef.current?.focus();
  };

  const onSelectGame = (gameId) => {
    API_CASINO.list
      .games({ gameId, currency })
      .then((e) => {
        const list = e.data.list;
        const game = list.find((el) => el.game_id == gameId);
        const isBonus = checkBonusGame(game);
        const urlGame = isBonus ? "bonus_game" : "game";
        const pathName = window.location.pathname;

        const runGame = () => {
          dispatch(setSaveSiteBeforeGame({ url: pathName, topScroll: window.scrollY }));
          navigate(`/${urlGame}/${gameId}`);
        };

        onChoseMode({ cbReal: runGame, cbDemo: runGame, game: game });
      })
      .catch(console.error)
      .finally(() => setOpen(false));
  };

  const onFindClick = (e) => {
    if (!isExtraSmall) return;

    e.preventDefault();
    e.stopPropagation();
    dispatch(setFindMobile(true));
  };

  const showResults = open && inputValue.length >= 3;
  const showEmpty = showResults && !isSearching && !gamesOptions.length;
  const showLoading = showResults && isSearching && !gamesOptions.length;

  return (
    <div className="find-game" ref={containerRef} onClick={onFindClick}>
      <label className="find-game__input-wrap" htmlFor={inputId}>
        <CusSelect
          readOnly={isExtraSmall && !isOpenMobileFind}
          inputRef={findRef}
          icon={<IconSearch />}
          input
          inputId={inputId}
          options={[]}
          placeholder={t("Find a game")}
          myInputValue={inputValue}
          onChange={() => {}}
          autoFocus={autoFocus}
          autoComplete={"off"}
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          onMyInputChange={onMyInputChange}
          iconJSXstart={
            inputValue ? (
              <button
                type="button"
                className="find-game__clear"
                onClick={onClear}
                aria-label={t("Очистить")}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 1L11 11M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ) : null
          }
        />
      </label>

      <ShowContainer condition={showResults && !setMyGamesOptions}>
        <div className="find-game__dropdown">
          {showLoading && (
            <div className="find-game__status">
              <span className="find-game__spinner" aria-hidden="true" />
              {t("Search")}...
            </div>
          )}

          {showEmpty && (
            <div className="find-game__status find-game__status--empty">{t("Nothing found")}</div>
          )}

          {!!gamesOptions.length && (
            <ul className="find-game__list">
              {gamesOptions.map((el) => (
                <li
                  key={el.value}
                  className="find-game__item"
                  onClick={() => onSelectGame(el.value)}>
                  <div className="find-game__avatar">
                    <CusImage height={36} url={el.image} alt={"image " + el.name} />
                  </div>

                  <span className="find-game__info">
                    <span className="find-game__name">{highlightMatch(el.name, textInput)}</span>
                    <span className="find-game__meta">
                      <span className="find-game__provider">{el.provider}</span>
                    </span>
                  </span>

                  <IconArrow className="find-game__arrow" />
                </li>
              ))}
            </ul>
          )}
        </div>
      </ShowContainer>
    </div>
  );
}
